import { useState } from "react";
import { useIntl } from "react-intl";
import { Divider, Message } from "../../../BP2Components";
import {
   Coordinates,
   Info,
   MapMarker,
   Save,
   Share,
   Time,
} from "../../../../assets/icons/businessCard";
import { PositionMarker } from "../../ListWidget/Lists/MyPositionMarker";
import GoogleMapReact from "google-map-react";
import { Icon_ChevronUp, Icon_ChevronDown } from "../../../BP2Components/Icons/icons";
import { FileOverlay } from "../FileOverlay/FileOverlay";
import clsx from "clsx";
import {
   getFileType,
   renderFileController,
} from "../../BlogCarouselWidget/BlogCarouselMediaHandler";

interface PreviewTabProps {
   businessCardData: Record<string, any>;
   files: string[];
}

export const PreviewTab = ({ businessCardData, files }: PreviewTabProps) => {
   const intl = useIntl();
   const [isDescriptionOpen, setIsDescriptionOpen] = useState<boolean>(false);
   const [selectedFile, setSelectedFile] = useState<string | null>(null);

   const [lat, lng] = businessCardData.wspolrzedne
      .split(",")
      .map((coord) => Number(coord.trim())) || [0, 0];

   const mapDefaultProps = {
      center: {
         lat,
         lng,
      },
      zoom: 11,
   };

   const touristInfo = [
      {
         isReadMore: false,
         image: MapMarker,
         text: `${businessCardData?.Klient_kli_ulica_tthra1} ${businessCardData?.Klient_kli_nrdomu_295tem}, ${businessCardData?.Klient_kli_kod_2c1smg} ${businessCardData?.Klient_kli_miejscowosc_obz367}`,
      },
      {
         isReadMore: false,
         image: Coordinates,
         text: businessCardData?.wspolrzedne,
      },
      {
         isReadMore: false,
         image: Time,
         text: `${intl.formatMessage({ id: "business.visitingTime" })}: ${Number(businessCardData?.czas)}h`,
      },
      {
         isReadMore: true,
         image: Info,
         text:
            businessCardData?.opis?.length > 300 && !isDescriptionOpen
               ? `${businessCardData.opis.slice(0, 300)}...`
               : businessCardData?.opis,
      },
   ];

   const handleNavigateInGoogleMaps = () => {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

      window.open(url, "_blank", "noopener,noreferrer");
   };

   const handleShareLocationInGoogleMaps = () => {
      const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
      const shareData = {
         url: googleMapsUrl,
      };

      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
         navigator.share(shareData).catch((error) => console.error("Share failed:", error));
      } else {
         window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
      }
   };

   const enrichedButtons = businessCardData?.buttons
      .map((techName) => {
         switch (techName) {
            case "getDirections":
               return {
                  techName,
                  icon: MapMarker,
                  label: "business.directions",
                  onClick: handleNavigateInGoogleMaps,
                  visible: true,
               };
            case "save":
               return {
                  techName,
                  icon: Save,
                  label: "forms.save",
                  onClick: undefined,
                  visible: false,
               };
            case "share":
               return {
                  techName,
                  icon: Share,
                  label: "global.share",
                  onClick: handleShareLocationInGoogleMaps,
                  visible: true,
               };
            default:
               return null;
         }
      })
      .filter((btn) => btn.visible);

   const handleReadMore = () => setIsDescriptionOpen((prev) => !prev);
   const handleImageClick = (imgUrl: string) => setSelectedFile(imgUrl);
   const closeOverlay = () => setSelectedFile(null);

   return (
      <>
         <div className="business-card__actions">
            {enrichedButtons.map(({ icon, label, onClick }) => (
               <button
                  key={label}
                  onClick={onClick}
                  className={clsx(
                     "business-card__action-button",
                     onClick === undefined && "business-card__action-button--disabled",
                  )}
                  disabled={onClick === undefined}
               >
                  <div>
                     <img src={icon} alt={label} />
                  </div>
                  <span>{intl.formatMessage({ id: label })}</span>
               </button>
            ))}
         </div>

         <Divider className="business-card__divider" />

         <ul className="business-card__addresses">
            {touristInfo.map((info, index) => (
               <li key={index} className="business-card__addresses-line">
                  <div className="business-card__addresses-line-content">
                     <img src={info.image} className="business-card__icon" alt={info.text} />
                     <p className="business-card__paragraph">{info.text}</p>
                  </div>

                  {info.isReadMore && info.text?.length > 200 && (
                     <button className="business-card__read-more" onClick={handleReadMore}>
                        {isDescriptionOpen ? (
                           <span>
                              {intl.formatMessage({ id: "business.readLess" })} <Icon_ChevronUp />
                           </span>
                        ) : (
                           <span>
                              {intl.formatMessage({ id: "business.readMore" })} <Icon_ChevronDown />
                           </span>
                        )}
                     </button>
                  )}
               </li>
            ))}
         </ul>

         <Divider className="business-card__divider" />

         {!!files.length && (
            <>
               <div>
                  <h3 className="business-card__section-title">
                     {intl.formatMessage({ id: "business.photosAndVideos" })}
                  </h3>
                  <div className="business-card__gallery">
                     {files.map((image, key) => {
                        const fileType = getFileType(image);
                        const keyId = `${image}-${key}`;

                        return (
                           <div className="business-card__gallery-item" key={keyId}>
                              {fileType === "movie" ? (
                                 renderFileController(image, fileType, 200, 200, {}, {})
                              ) : (
                                 <img src={image} onClick={() => handleImageClick(image)} />
                              )}
                           </div>
                        );
                     })}
                  </div>
               </div>
               <Divider className="business-card__divider" />{" "}
            </>
         )}

         <div className="business-card__map">
            <h3 className="business-card__section-title">
               {intl.formatMessage({ id: "sidebar.right.Map" })}
            </h3>
            <div className="business-card__map-container">
               <GoogleMapReact
                  bootstrapURLKeys={{
                     key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
                  }}
                  defaultCenter={mapDefaultProps.center}
                  defaultZoom={mapDefaultProps.zoom}
               >
                  <PositionMarker lat={lat} lng={lng} text="My Marker" />
               </GoogleMapReact>
            </div>
         </div>
         {selectedFile && <FileOverlay file={selectedFile} onClose={closeOverlay} />}
      </>
   );
};
